import "../customer.scss";

import ProgressBarWithLabel from "@core/ui/centered-progress-bar/centered-progress-bar";
import GridItem, { GridItemModel } from "@core/ui/grid-item/grid-item";
import {
  GetResponseRewardCampaignModel,
  GetRewardCampaignModel,
} from "@factory/model/query-model-reward";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { useQyGetRewardCampaign } from "@factory/query/reward.query";
import ErrorSection from "@shared/ui/error-section/error-section";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { DataView } from "primereact/dataview";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";

import TagColor from "../../../core/ui/tag-color/tag-color";
import { useListCardsContext } from "./list-cards.context";

export function CustomerListCampaign() {
  // THIS IS THE LOCAL DECLARATION
  const { formatDateLocal } = useDateTimeContext();
  const { balanceData, navigate, setPreviewRedeem, selectedCard, tenant } =
    useListCardsContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [, setPageNumber] = useState(0);
  const [first, setFirst] = useState(0);
  const [responseData, setResponseData] = useState<GetRewardCampaignModel[]>(
    []
  );
  const [imageBlobByCode, setImageBlobByCode] = useState<
    Record<string, string | null>
  >({});
  const imageBlobUrlsRef = useRef<string[]>([]);
  const [layout] = useState<
    "grid" | "list" | (string & Record<string, unknown>)
  >("grid");

  const handleGetCampaignSuccess = (
    response: GetResponseRewardCampaignModel
  ) => {
    setResponseData(response?.data || []);
  };

  useEffect(() => {
    let cancelled = false;

    imageBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    imageBlobUrlsRef.current = [];
    setImageBlobByCode({});

    responseData.forEach((element) => {
      if (!element?.image) {
        return;
      }

      viewAsset(element.image)
        .then((file) => {
          if (cancelled) {
            return;
          }
          const blob = URL.createObjectURL(file);
          imageBlobUrlsRef.current.push(blob);
          setImageBlobByCode((prev) => ({ ...prev, [element.code]: blob }));
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setImageBlobByCode((prev) => ({ ...prev, [element.code]: null }));
        });
    });

    return () => {
      cancelled = true;
      imageBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      imageBlobUrlsRef.current = [];
    };
  }, [responseData]);
  const {
    data: campaignResponse,
    isLoading,
    isError,
    error,
  } = useQyGetRewardCampaign(
    selectedCard?.card_alias ?? "",
    tenant,
    !!selectedCard?.card_alias,
    handleGetCampaignSuccess
  );

  // THIS IS THE LOCAL FUNCTION
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handlePreviewRedeem = (item: GridItemModel) => {
    setPreviewRedeem(item);
    navigate("redeem");
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const listItem = (campaign: GetRewardCampaignModel) => {
    return (
      <div
        className="flex shadow-md rounded-md overflow-hidden border pr-4"
        key={campaign.code}
      >
        <div className={"flex border-top-1 surface-border w-full"}>
          {campaign.image && imageBlobByCode[campaign.code] === undefined ? (
            <div className="w-9 sm:w-16 lg:w-28 min-h-28 shadow-md mx-auto border-round flex-shrink-0">
              <Skeleton width="100%" height="7rem" />
            </div>
          ) : (
            <img
              className="w-9 sm:w-16 lg:w-28 min-h-28 shadow-md object-cover block xl:block mx-auto border-round"
              src={imageBlobByCode[campaign.code] ?? undefined}
              alt={campaign.name}
            />
          )}
          <div className="flex flex-col flex-1 p-4 overflow-hidden">
            <h4>{campaign.name}</h4>
            <p className="text-sm text-gray-400">
              Expires on {formatDateLocal(campaign.end_date || "")}
            </p>
            <div className="w-96">
              <p className="text-ellipsis truncate">{campaign?.description}</p>
            </div>
          </div>
          <div className="w-1/5 py-4">
            <ProgressBarWithLabel
              current={balanceData?.current_points}
              total={campaign.points}
              unit="points"
            />
            <div className="flex justify-center mb-1">
              <TagColor value={campaign.constraint ?? ""} />
            </div>
            <ProgressBarWithLabel
              current={balanceData?.current_stamp}
              total={campaign.stamp}
              unit="stamps"
            />
          </div>
        </div>
      </div>
    );
  };
  const gridItem = (campaign: GetRewardCampaignModel) => {
    const resolvedImage = imageBlobByCode[campaign.code];
    const imagePending =
      Boolean(campaign.image) && resolvedImage === undefined;
    const model = {
      name: campaign.name,
      image: resolvedImage ?? "",
      description: campaign.description,
      points: campaign.points,
      stamp: campaign.stamp,
      expirationDate: campaign.end_date,
      code: campaign.code,
      constraint: campaign.constraint,
      limitPerMonth: campaign.limit_per_month,
      monthlyRedeemCount: campaign.monthly_redeem_count,
      metadata: campaign,
    } as GridItemModel;
    return (
      <GridItem
        campaign={model}
        key={campaign.code}
        onRedeem={handlePreviewRedeem}
        shouldHideCriteria={true}
        imagePending={imagePending}
      />
    );
  };
  const itemTemplate = (
    campaign: GetRewardCampaignModel,
    layout: "grid" | "list"
  ) => {
    if (!campaign) {
      return;
    }
    if (layout === "list") return listItem(campaign);
    else if (layout === "grid") return gridItem(campaign);
  };
  const listTemplate = (
    campaigns: GetRewardCampaignModel[],
    layout: "grid" | "list"
  ): any => {
    return (
      <div
        className={`grid ${
          layout === "grid" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        } gap-4 mt-4`}
      >
        {campaigns.map((campaign) => itemTemplate(campaign, layout))}
      </div>
    );
  };
  const grid = (
    <section>
      <h5 className="mb-2">
        Total: <b>{campaignResponse?.data?.length}</b>
      </h5>
      <DataView
        value={responseData}
        listTemplate={listTemplate}
        layout={layout}
        paginator
        rows={rowLimit}
        first={first}
        totalRecords={campaignResponse?.data?.length}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListCampaign" className="campaign">
      <div className="md:p-4 bg-white">
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}

        <Outlet />
      </div>
    </div>
  );
}

export default CustomerListCampaign;
